import dayjs from "dayjs";
import * as XLSX from "xlsx";

import type { AccountMoveLine, AccountMovement } from "~/types/contabilidad";

interface MovementExportData {
  movement: AccountMovement;
  lines: AccountMoveLine[];
}

/**
 * Exports a single accounting movement (comprobante contable) to Excel
 */
export const exportMovimientoToExcel = ({
  movement,
  lines,
}: MovementExportData): void => {
  const workbook = XLSX.utils.book_new();

  // Header section data
  const headerData = [
    ["COMPROBANTE CONTABLE"],
    [],
    ["Número:", movement.move_number || "-"],
    ["Fecha:", dayjs(movement.move_date).format("DD/MM/YYYY")],
    ["Referencia:", movement.ref || "-"],
    [
      "Diario:",
      movement.journal
        ? `${movement.journal.code} - ${movement.journal.name}`
        : "-",
    ],
    ["Empresa:", movement.company?.name || "-"],
    ["Estado:", movement.is_published ? "Publicado" : "Borrador"],
    [],
  ];

  // Lines table header
  const linesHeader = ["Cuenta", "Nombre", "Descripción", "Debe", "Haber"];

  // Lines data
  const linesData = lines.map((line) => [
    line.account?.code || "-",
    line.account?.name || "-",
    line.description || "-",
    Number(line.debit),
    Number(line.credit),
  ]);

  // Calculate totals
  const totalDebit = lines.reduce((sum, line) => sum + Number(line.debit), 0);
  const totalCredit = lines.reduce((sum, line) => sum + Number(line.credit), 0);

  // Totals row
  const totalsRow = ["", "", "TOTAL", totalDebit, totalCredit];

  // Combine all data
  const sheetData = [
    ...headerData,
    linesHeader,
    ...linesData,
    totalsRow,
  ];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  // Set column widths
  worksheet["!cols"] = [
    { wch: 12 }, // Cuenta
    { wch: 35 }, // Nombre
    { wch: 45 }, // Descripción
    { wch: 18 }, // Debe
    { wch: 18 }, // Haber
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Comprobante");

  // Generate filename
  const moveNumber = movement.move_number || "sin_numero";
  const moveDate = dayjs(movement.move_date).format("YYYY-MM-DD");
  const filename = `comprobante_${moveNumber}_${moveDate}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, filename);
};
